import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";

import socket from "../socket";
import { getApiBase } from "../utils/apiBase";

const PUBLIC_API_BASE = getApiBase();
export const MESSAGES_REFRESH_EVENT = "qubiq-messages-refresh";

const useMessagesUnreadCount = () => {
  const { isSignedIn, getToken, userId } = useAuth();
  const [messageCount, setMessageCount] = useState(0);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let refreshPending = false;
    let refreshTimer;
    const controller = new AbortController();

    const loadMessageCount = async () => {
      if (cancelled) return;
      if (inFlight) { refreshPending = true; return; }
      if (!isSignedIn) {
        if (!cancelled) setMessageCount(0);
        return;
      }

      inFlight = true;
      try {
        const token = await getTokenRef.current().catch(() => null);
        if (cancelled) return;
        if (!token) {
          if (!cancelled) setMessageCount(0);
          return;
        }

        const response = await fetch(`${PUBLIC_API_BASE}/messages/unread-count`, {
          credentials: "include",
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || "messages_unread_count_failed");
        }

        if (!cancelled) {
          setMessageCount(Math.max(0, Number(data?.count ?? data?.unreadCount ?? 0)));
        }
      } catch {
        if (!cancelled) setMessageCount(0);
      } finally {
        inFlight = false;
        if (refreshPending && !cancelled) {
          refreshPending = false;
          handleMessagesRefresh();
        }
      }
    };

    const subscribeToMessages = async () => {
      if (!isSignedIn) return;
      const token = await getTokenRef.current().catch(() => null);
      if (cancelled || !token) return;
      socket.emit("messages:subscribe", { token });
    };

    const handleMessagesRefresh = () => {
      if (cancelled || document.visibilityState === "hidden") return;
      // A read receipt arrives both locally and through the socket.
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(loadMessageCount, 100);
    };

    loadMessageCount();
    subscribeToMessages();

    const intervalId = window.setInterval(loadMessageCount, 30000);
    window.addEventListener(MESSAGES_REFRESH_EVENT, handleMessagesRefresh);
    window.addEventListener("focus", handleMessagesRefresh);
    document.addEventListener("visibilitychange", handleMessagesRefresh);
    socket.on("messages:new", handleMessagesRefresh);
    socket.on("messages:read", handleMessagesRefresh);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(refreshTimer);
      window.clearInterval(intervalId);
      window.removeEventListener(MESSAGES_REFRESH_EVENT, handleMessagesRefresh);
      window.removeEventListener("focus", handleMessagesRefresh);
      document.removeEventListener("visibilitychange", handleMessagesRefresh);
      socket.off("messages:new", handleMessagesRefresh);
      socket.off("messages:read", handleMessagesRefresh);
    };
  }, [isSignedIn, userId]);

  return {
    messageCount,
    hasUnreadMessages: messageCount > 0,
  };
};

export default useMessagesUnreadCount;
